import React from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Record, STATUS_COLORS } from './types';

interface RecordItemProps {
  record: Record;
  onClick?: (record: Record) => void;
  className?: string;
}

const RecordItem: React.FC<RecordItemProps> = ({ record, onClick, className = '' }) => {
  const handleClick = () => {
    onClick?.(record);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const statusConfig = record.status ? STATUS_COLORS[record.status] || STATUS_COLORS.default : null;

  return (
    <div
      className={cn(
        'flex items-center justify-between py-3 px-1 cursor-pointer hover:bg-gray-50 rounded-md transition-colors duration-200',
        className
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`${record.title} record`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{record.title}</p>
        {record.subtitle && (
          <p className="text-sm text-gray-500 truncate mt-1">{record.subtitle}</p>
        )}
      </div>

      <div className="flex items-center ml-4">
        {record.value && <p className="text-sm text-gray-700 mr-4">{record.value}</p>}
        {record.date && <p className="text-sm text-gray-500 mr-4 hidden sm:block">{record.date}</p>}
        {statusConfig && (
          <Badge
            variant="outline"
            className={cn(
              'font-semibold border',
              statusConfig.bg,
              statusConfig.text,
              statusConfig.border
            )}
          >
            {record.status}
          </Badge>
        )}
      </div>
    </div>
  );
};

RecordItem.displayName = 'RecordItem';

export { RecordItem };
